import React, { useState, lazy, Suspense } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Spinner } from 'react-bootstrap';

// Lazy loaded components
const HeavyComponent1 = lazy(() => {
  console.log('📦 Lazy Loading: HeavyComponent1 chunk loading...');
  return new Promise(resolve => {
    setTimeout(() => {
      console.log('✅ HeavyComponent1 loaded!');
      resolve({
        default: () => (
          <Card className="mb-3">
            <Card.Header className="bg-success text-white">Heavy Component 1</Card.Header>
            <Card.Body>
              <h5>I was lazy loaded! 🎉</h5>
              <p>This component was loaded on demand, not during initial page load.</p>
              <Alert variant="success">
                Check the console to see the loading message!
              </Alert>
            </Card.Body>
          </Card>
        )
      });
    }, 1500); // Simulate network delay
  });
});

const HeavyComponent2 = lazy(() => {
  console.log('📦 Lazy Loading: HeavyComponent2 chunk loading...');
  return new Promise(resolve => {
    setTimeout(() => {
      console.log('✅ HeavyComponent2 loaded!');
      resolve({
        default: () => (
          <Card className="mb-3">
            <Card.Header className="bg-primary text-white">Heavy Component 2</Card.Header>
            <Card.Body>
              <h5>Another lazy loaded component! 🚀</h5>
              <p>Each lazy component is loaded in its own chunk.</p>
              <pre className="code-example">
{`const Component = lazy(() => 
  import('./HeavyComponent')
);

<Suspense fallback={<Loading />}>
  <Component />
</Suspense>`}
              </pre>
            </Card.Body>
          </Card>
        )
      });
    }, 2000);
  });
});

// Loading Fallback Component
const LoadingFallback = () => (
  <Card className="mb-3">
    <Card.Body className="text-center py-5">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3">Loading component...</p>
    </Card.Body>
  </Card>
);

const Lesson19_LazyLoading = () => {
  const [showComponent1, setShowComponent1] = useState(false);
  const [showComponent2, setShowComponent2] = useState(false);

  React.useEffect(() => {
    console.log('📖 Lesson 19: Lazy Loading & Code Splitting - Loaded');
    console.log('🎯 Lazy loading helps reduce initial bundle size');
  }, []);

  const loadComponent1 = () => {
    console.log('');
    console.log('━━━━━━━━ Loading Component 1 ━━━━━━━━');
    setShowComponent1(true);
  };

  const loadComponent2 = () => {
    console.log('');
    console.log('━━━━━━━━ Loading Component 2 ━━━━━━━━');
    setShowComponent2(true);
  };

  return (
    <LessonLayout 
      lessonNumber={19}
      title="Lazy Loading & Code Splitting" 
      description="Optimize performance with code splitting and lazy loading"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>React.lazy() for dynamic imports</li>
          <li>Suspense component for loading states</li>
          <li>Code splitting benefits</li>
          <li>Route-based code splitting</li>
          <li>Performance optimization</li>
        </ul>
      </Alert>

      <Card className="mt-4 mb-3">
        <Card.Header>Load Components On Demand</Card.Header>
        <Card.Body>
          <div className="d-flex gap-2">
            <Button onClick={loadComponent1} variant="primary" disabled={showComponent1}>
              {showComponent1 ? '✅ Component 1 Loaded' : 'Load Component 1'}
            </Button>
            <Button onClick={loadComponent2} variant="success" disabled={showComponent2}>
              {showComponent2 ? '✅ Component 2 Loaded' : 'Load Component 2'}
            </Button>
          </div>
          <Alert variant="info" className="mt-3 mb-0">
            Click buttons to dynamically load components. Watch the console for loading messages!
          </Alert>
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          {showComponent1 && (
            <Suspense fallback={<LoadingFallback />}>
              <HeavyComponent1 />
            </Suspense>
          )}
        </Col>

        <Col md={6}>
          {showComponent2 && (
            <Suspense fallback={<LoadingFallback />}>
              <HeavyComponent2 />
            </Suspense>
          )}
        </Col>
      </Row>

      <h4 className="mt-4">💻 Code Splitting Patterns</h4>
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Component-based Splitting</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`import React, { lazy, Suspense } from 'react';

// Lazy load component
const MyComponent = lazy(() => 
  import('./MyComponent')
);

function App() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <MyComponent />
    </Suspense>
  );
}`}
              </pre>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Route-based Splitting</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';

const Home = lazy(() => import('./Home'));
const About = lazy(() => import('./About'));

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
      </Routes>
    </Suspense>
  );
}`}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h4 className="mt-4">📊 Benefits of Code Splitting</h4>
      <Card>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Alert variant="success">
                <h6>✅ Reduced Initial Load</h6>
                <p className="mb-0 small">Smaller initial bundle size means faster first load</p>
              </Alert>
            </Col>
            <Col md={4}>
              <Alert variant="primary">
                <h6>🚀 Better Performance</h6>
                <p className="mb-0 small">Load code only when needed, improving performance</p>
              </Alert>
            </Col>
            <Col md={4}>
              <Alert variant="info">
                <h6>📦 Better Caching</h6>
                <p className="mb-0 small">Changed components don't invalidate entire bundle</p>
              </Alert>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Split code at route level first (biggest impact)</li>
          <li>Use Suspense to provide loading feedback</li>
          <li>Lazy load heavy components (charts, editors, modals)</li>
          <li>Don't lazy load critical above-the-fold content</li>
          <li>Consider preloading for better UX: <code>componentRef.preload()</code></li>
          <li>Works great with React Router for route-based splitting</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson19_LazyLoading;
